import React, { useState } from 'react';
import apiClient from '../api/client';
import { 
    TextField, 
    Button, 
    Box, 
    Paper, 
    Typography,
    Snackbar,
    Alert,
    InputAdornment,
    IconButton
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const AddSkuByUrl = () => {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [submitting, setSubmitting] = useState(false);
  
    // URL validation function
    const isValidUrl = (string) => {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    };
  
    // Handle URL input change
    const handleUrlChange = (event) => {
      setUrl(event.target.value);
      if (error) setError('');
    };
  
    // Clear the input field
    const handleClearInput = () => {
      setUrl('');
      setError('');
    };
  
    // Handle form submission
    const handleAddSku = () => {
      if (!url.trim()) {
        setError('URL is required');
        return;
      }
  
      if (!isValidUrl(url)) {
        setError('Please enter a valid URL (e.g., https://example.com/product)');
        return;
      }
  
      addSKU(url.trim());
    };
  
    const handleSnackbarClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpenSnackbar(false);
    };
  
    const addSKU = async (Aurl) => {
        setSubmitting(true);
        try {
            const endpoint = '/sku-refresh?url=' + encodeURIComponent(Aurl);
            await apiClient.get(endpoint);
            setSuccessMessage('SKU queued successfully for discovery and ingestion!');
            setOpenSnackbar(true);
            setUrl('');
        } catch (error) {
            console.error('Error adding SKU:', error);
            setError('Failed to add SKU. Please verify the URL and try again.');
            setSuccessMessage('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 2 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <AddCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Add Product SKU by URL
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Paste any distributor or vendor product web link to immediately queue it for price, stock, and specification synchronization.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Product URL"
              variant="outlined"
              value={url}
              onChange={handleUrlChange}
              error={!!error}
              helperText={error}
              placeholder="https://vendor-domain.com/item/12345"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: url && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear input"
                      onClick={handleClearInput}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyUp={(event) => {
                if (event.key === 'Enter') handleAddSku();
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleClearInput}
                disabled={!url || submitting}
              >
                Clear
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAddSku}
                disabled={submitting}
                sx={{ minWidth: 120 }}
              >
                {submitting ? 'Adding...' : 'Add SKU'}
              </Button>
            </Box>
          </Box>
          
          <Snackbar 
            open={openSnackbar} 
            autoHideDuration={5000} 
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleSnackbarClose} 
              severity="success" 
              sx={{ width: '100%', borderRadius: 2 }}
            >
              {successMessage}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    );
};

export default AddSkuByUrl;